import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Trophy, Plus, Copy, ExternalLink, Users, Share2 } from "lucide-react";
import { getTenantSlugFromPath } from "@/lib/tenant";

const STATUS_BADGE = {
  draft:  { label: "Draft",  className: "bg-amber-100 text-amber-800 border-amber-200" },
  active: { label: "Active", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  closed: { label: "Closed", className: "bg-gray-100 text-gray-700 border-gray-200" },
};

export default function Competitions() {
  const navigate = useNavigate();
  const tenantSlug = getTenantSlugFromPath();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/competitions`);
      setItems(res.data || []);
    } catch (e) {
      toast.error("Failed to load competitions");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error("Please enter a competition name"); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/competitions`, { name: name.trim() });
      toast.success("Competition created");
      setCreateOpen(false);
      setName("");
      navigate(`/admin/competitions/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    }
    setSaving(false);
  };

  const publicUrl = (slug) => `${window.location.origin}/${tenantSlug}/competition/${slug}`;

  const copyLink = async (slug) => {
    try {
      await navigator.clipboard.writeText(publicUrl(slug));
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <div className="space-y-6" data-testid="competitions-page">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2" style={{ fontFamily: "Playfair Display" }}>
            <Trophy size={22} className="text-primary" />
            Competitions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create shareable entry forms — collect leads, pick winners, and grow your audience.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-competition-btn" className="rounded-xl">
          <Plus size={16} className="mr-1.5" /> New Competition
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center border-dashed" data-testid="competitions-empty">
          <Trophy size={36} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-foreground">No competitions yet</p>
          <p className="text-xs text-muted-foreground mt-1 mb-4">
            Spin up your first competition — set a prize, share the link, watch entries roll in.
          </p>
          <Button onClick={() => setCreateOpen(true)} data-testid="empty-create-btn" className="rounded-xl">
            <Plus size={14} className="mr-1.5" /> Create Competition
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => {
            const status = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
            return (
              <Card key={c.id} className="overflow-hidden hover:shadow-md transition-shadow" data-testid={`competition-card-${c.id}`}>
                <div
                  className="h-28 bg-gradient-to-br from-pink-100 to-purple-100 relative"
                  style={c.banner_image_url ? { backgroundImage: `url(${c.banner_image_url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
                >
                  <div className="absolute top-2 right-2">
                    <Badge className={`text-[10px] rounded-full px-2 ${status.className}`}>{status.label}</Badge>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-sm leading-tight">{c.name}</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5 truncate">/{tenantSlug}/competition/{c.slug}</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Users size={12} /> {c.entry_count || 0} entries
                    </span>
                    {c.winner_entry_id && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <Trophy size={12} /> Winner picked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg text-xs flex-1"
                      onClick={() => copyLink(c.slug)}
                      data-testid={`copy-link-${c.id}`}
                    >
                      <Copy size={12} className="mr-1.5" /> Copy
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-lg text-xs"
                      onClick={() => window.open(publicUrl(c.slug), "_blank")}
                      data-testid={`open-link-${c.id}`}
                      title="Open public form"
                    >
                      <ExternalLink size={12} />
                    </Button>
                    <Link to={`/admin/competitions/${c.id}`} className="flex-1">
                      <Button size="sm" className="rounded-lg text-xs w-full" data-testid={`manage-${c.id}`}>
                        Manage
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Competition</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label htmlFor="comp-name">Competition Name</Label>
              <Input
                id="comp-name"
                data-testid="new-competition-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Summer Giveaway"
                className="mt-1"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                You can customise the rest (prize, image, fields) on the next screen.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="confirm-create-competition" className="rounded-xl">
              {saving ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
